import { ResponseBuildMetricsSummary } from '@trace/types';
import { useContext, useEffect, useState } from 'react';
import { useQuery, UseQueryResult } from 'react-query';
import { AuthContextTrace } from '../Authenticate/AuthenticateTrace';
import { callCloud } from '../../fetch/fetch';
import appendURLSearchParams from '../../utils/appendURLSearchParams';
import getDateRange from '../../utils/time/getTimeRange';
import { URLStateContext } from '../URLState/URLState';

interface HookPayload extends ResponseBuildMetricsSummary {
  isFetched: boolean;
}

export type HookResult = Pick<UseQueryResult, 'isError' | 'isFetching' | 'refetch'> & HookPayload;

interface Options {
  end?: number | null;
  filterBy?: string;
  groupBy?: string;
  start?: number | null;
}

const useBuildsSummary = (options: Options = {}): HookResult => {
  const { end: endOverride, filterBy, groupBy, start: startOverride } = options;
  const { token: traceToken } = useContext(AuthContextTrace);
  const { appSlug, dateFrom, dateTo, duration } = useContext(URLStateContext);
  const { dateFrom: start, dateTo: end } = getDateRange(duration, dateFrom, dateTo);
  const [payload, setPayload] = useState<ResponseBuildMetricsSummary>({
    data: [],
    end,
    start,
  });

  const { data, isError, isFetching, isSuccess, refetch } = useQuery<ResponseBuildMetricsSummary>(
    [`buildMetricsSummary`, { appSlug, end, endOverride, filterBy, groupBy, start, startOverride }],
    () =>
      callCloud<ResponseBuildMetricsSummary>(
        'GET',
        `/trace/api/metrics/builds/summary?${appendURLSearchParams(
          '',
          ['appSlug', appSlug],
          ['filter_by', filterBy],
          ['group_by', groupBy],
          ['start', (startOverride || start).toString()],
          ['end', (endOverride || end).toString()],
        )}`,
        traceToken || '',
      ),
  );

  useEffect(() => {
    if (data && !isFetching) {
      setPayload(data);
    } else if (isFetching) {
      setPayload({
        data: [],
        end,
        start,
      });
    }
  }, [data, isFetching, start, end]);

  return {
    isError,
    isFetched: isSuccess,
    isFetching,
    refetch,
    ...payload,
  };
};

export default useBuildsSummary;
